import {
  containsCatalogKeyword,
  isLikelyProductType,
  normalizeCatalogText,
} from '@/lib/catalog/guards';
import { SLOT_MAP } from '@/lib/pc-builder/constants';
import type {
  CompatibilityIssue,
  ParsedCompatibilitySpecs,
  PcBuilderProduct,
  PcSlotKey,
} from '@/lib/pc-builder/types';

function toTextArray(product: PcBuilderProduct): string[] {
  const base = [
    product.name,
    product.specs || '',
    product.sku || '',
    product.brand_name || '',
    product.category_name || '',
    product.category_slug || '',
    product.tags.join(' '),
  ];

  const specLines = Object.entries(product.full_specs || {}).map(
    ([k, v]) => `${k} ${String(v || '')}`
  );
  return [...base, ...specLines].filter(Boolean).map((v) => normalizeCatalogText(v));
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function pickMaxNumber(values: number[]): number | null {
  if (!values.length) return null;
  return Math.max(...values);
}

function parseSockets(text: string): string[] {
  const out: string[] = [];
  const socketMatches = text.match(
    /\b(am4|am5|am3\+|am3|am2\+|am2|tr4|strx4|swrx8|fm2\+|fm2|lga\s*\d{3,4}|socket\s*\d{3,4})\b/g
  );
  if (socketMatches) {
    for (const item of socketMatches) {
      out.push(item.replace(/\s+/g, '').replace('socket', 'lga').toUpperCase());
    }
  }

  if (text.includes('intel 1700')) out.push('LGA1700');
  if (text.includes('intel 1851')) out.push('LGA1851');
  if (text.includes('intel 1200')) out.push('LGA1200');
  if (text.includes('intel 1151')) out.push('LGA1151');
  if (text.includes('intel 2066')) out.push('LGA2066');
  if (text.includes('amd am4')) out.push('AM4');
  if (text.includes('amd am5')) out.push('AM5');

  return unique(out);
}

function parseRamTypes(text: string): string[] {
  const out: string[] = [];
  if (text.includes('ddr5')) out.push('DDR5');
  if (text.includes('ddr4')) out.push('DDR4');
  if (text.includes('ddr3')) out.push('DDR3');
  return unique(out);
}

function parseFormFactors(text: string): string[] {
  const out: string[] = [];
  if (/(^|\s)e[\s-]?atx(\s|$)/i.test(text)) out.push('EATX');
  if (/(^|\s)micro[\s-]?atx(\s|$)|(^|\s)m[\s-]?atx(\s|$)|(^|\s)matx(\s|$)/i.test(text))
    out.push('MATX');
  if (/(^|\s)mini[\s-]?itx(\s|$)|(^|\s)itx(\s|$)/i.test(text)) out.push('ITX');
  if (/(^|\s)atx(\s|$)/i.test(text)) out.push('ATX');
  return unique(out);
}

function parseChipsets(text: string): string[] {
  const out: string[] = [];
  const matches = text.match(/\b([abhxz]\d{2,3}|trx\d{2}|w\d{3}|z\d{3}|x\d{3}|b\d{3}|h\d{3})\b/gi);
  if (matches) {
    for (const item of matches) out.push(item.toUpperCase());
  }
  return unique(out);
}

function parseWatt(text: string): number | null {
  const watts: number[] = [];
  for (const m of text.matchAll(/\b(\d{3,4})\s*w\b/gi)) {
    const value = Number(m[1]);
    if (value >= 250 && value <= 2000) watts.push(value);
  }
  return pickMaxNumber(watts);
}

function parseCapacityGb(text: string): number | null {
  const values: number[] = [];
  for (const m of text.matchAll(/\b(\d{2,5})\s*gb\b/gi)) {
    const value = Number(m[1]);
    if (value >= 60 && value <= 8192) values.push(value);
  }

  for (const m of text.matchAll(/\b(\d{1,2}(?:\.\d+)?)\s*tb\b/gi)) {
    const value = Math.round(Number(m[1]) * 1024);
    if (value >= 256 && value <= 65536) values.push(value);
  }

  return pickMaxNumber(values);
}

function parseSizeMm(text: string, min = 120, max = 600): number | null {
  const values: number[] = [];
  for (const m of text.matchAll(/\b(\d{2,3})\s*mm\b/gi)) {
    const value = Number(m[1]);
    if (value >= min && value <= max) values.push(value);
  }
  return pickMaxNumber(values);
}

function estimateCpuPower(text: string): number {
  const parsed = parseWatt(text);
  if (parsed && parsed <= 350) return parsed;
  if (text.includes('core i9') || text.includes('ryzen 9')) return 170;
  if (text.includes('core i7') || text.includes('ryzen 7')) return 125;
  if (text.includes('core i5') || text.includes('ryzen 5')) return 95;
  return 65;
}

function estimateGpuPower(text: string): number {
  const parsed = parseWatt(text);
  if (parsed && parsed <= 600) return parsed;
  if (text.includes('rtx 4090')) return 450;
  if (text.includes('rtx 4080') || text.includes('rx 7900')) return 320;
  if (text.includes('rtx 4070') || text.includes('rx 7800')) return 250;
  if (text.includes('rtx 4060') || text.includes('rx 7700')) return 180;
  if (text.includes('gtx') || text.includes('rx 6600') || text.includes('arc')) return 150;
  return 0;
}

export function parseCompatibilitySpecs(product: PcBuilderProduct): ParsedCompatibilitySpecs {
  const text = toTextArray(product).join(' ');
  const sockets = parseSockets(text);
  const ramTypes = parseRamTypes(text);
  const formFactors = parseFormFactors(text);
  const chipsets = parseChipsets(text);

  const psuWattCandidate = SLOT_MAP.psu.searchKeywords.some((kw) =>
    containsCatalogKeyword(text, kw)
  )
    ? parseWatt(text)
    : null;

  const isMainboard =
    (product.category_slug && containsCatalogKeyword(product.category_slug, 'mainboard')) ||
    isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'mainboard');

  const isCase =
    (product.category_slug && containsCatalogKeyword(product.category_slug, 'case')) ||
    isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'case');

  const isCpu =
    (product.category_slug && containsCatalogKeyword(product.category_slug, 'cpu')) ||
    isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'cpu');

  const isVga =
    (product.category_slug && containsCatalogKeyword(product.category_slug, 'vga')) ||
    isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'vga');

  const mainboardFormFactors = isMainboard ? formFactors : [];
  const caseSupportFormFactors = isCase ? formFactors : [];
  const coolerSockets = parseSockets(text);

  const vgaLengthMm = isVga ? parseSizeMm(text, 150, 500) : null;
  const caseVgaLimitMm =
    isCase && (text.includes('gpu') || text.includes('vga') || text.includes('card man hinh'))
      ? parseSizeMm(text, 180, 550)
      : null;

  const estimatedPowerDrawW = isCpu
    ? estimateCpuPower(text)
    : isVga
      ? estimateGpuPower(text)
      : psuWattCandidate;

  return {
    sockets,
    ramTypes,
    mainboardFormFactors,
    caseSupportFormFactors,
    psuWatt: psuWattCandidate,
    coolerSockets,
    vgaLengthMm,
    caseVgaLimitMm,
    storageCapacityGb: parseCapacityGb(text),
    chipsetSeries: chipsets,
    estimatedPowerDrawW,
  };
}

function intersect(a: string[], b: string[]) {
  if (!a.length || !b.length) return true;
  const bSet = new Set(b);
  return a.some((x) => bSet.has(x));
}

function getSelected(
  selected: Partial<Record<PcSlotKey, PcBuilderProduct>>,
  slot: PcSlotKey
): PcBuilderProduct | null {
  return selected[slot] || null;
}

export function evaluateCompatibility(
  selected: Partial<Record<PcSlotKey, PcBuilderProduct>>,
  quantities: Partial<Record<PcSlotKey, number>>
): { issues: CompatibilityIssue[]; estimatedLoadW: number } {
  const issues: CompatibilityIssue[] = [];

  const cpu = getSelected(selected, 'cpu');
  const mainboard = getSelected(selected, 'mainboard');
  const ram = getSelected(selected, 'ram');
  const psu = getSelected(selected, 'psu');
  const pcCase = getSelected(selected, 'case');
  const cooler = getSelected(selected, 'cooler');
  const vga = getSelected(selected, 'vga');

  const cpuSpecs = cpu ? parseCompatibilitySpecs(cpu) : null;
  const mainSpecs = mainboard ? parseCompatibilitySpecs(mainboard) : null;
  const ramSpecs = ram ? parseCompatibilitySpecs(ram) : null;
  const psuSpecs = psu ? parseCompatibilitySpecs(psu) : null;
  const caseSpecs = pcCase ? parseCompatibilitySpecs(pcCase) : null;
  const coolerSpecs = cooler ? parseCompatibilitySpecs(cooler) : null;
  const vgaSpecs = vga ? parseCompatibilitySpecs(vga) : null;

  if (cpuSpecs && mainSpecs && !intersect(cpuSpecs.sockets, mainSpecs.sockets)) {
    issues.push({
      id: 'cpu-mainboard-socket',
      severity: 'error',
      title: 'CPU và Mainboard chưa cùng socket',
      detail: `CPU: ${cpuSpecs.sockets.join(', ') || 'không rõ'} | Mainboard: ${mainSpecs.sockets.join(', ') || 'không rõ'}`,
      slots: ['cpu', 'mainboard'],
    });
  }

  if (mainSpecs && ramSpecs && !intersect(mainSpecs.ramTypes, ramSpecs.ramTypes)) {
    issues.push({
      id: 'mainboard-ram-type',
      severity: 'error',
      title: 'Mainboard và RAM khác chuẩn DDR',
      detail: `Mainboard: ${mainSpecs.ramTypes.join(', ') || 'không rõ'} | RAM: ${ramSpecs.ramTypes.join(', ') || 'không rõ'}`,
      slots: ['mainboard', 'ram'],
    });
  }

  if (
    mainSpecs &&
    caseSpecs &&
    mainSpecs.mainboardFormFactors.length > 0 &&
    caseSpecs.caseSupportFormFactors.length > 0 &&
    !intersect(mainSpecs.mainboardFormFactors, caseSpecs.caseSupportFormFactors)
  ) {
    issues.push({
      id: 'mainboard-case-size',
      severity: 'error',
      title: 'Case không hỗ trợ kích thước mainboard',
      detail: `Mainboard: ${mainSpecs.mainboardFormFactors.join(', ')} | Case: ${caseSpecs.caseSupportFormFactors.join(', ')}`,
      slots: ['mainboard', 'case'],
    });
  }

  if (cpuSpecs && coolerSpecs && !intersect(cpuSpecs.sockets, coolerSpecs.coolerSockets)) {
    issues.push({
      id: 'cpu-cooler-socket',
      severity: 'warning',
      title: 'Tản nhiệt có thể không khớp socket CPU',
      detail: `CPU: ${cpuSpecs.sockets.join(', ') || 'không rõ'} | Tản: ${coolerSpecs.coolerSockets.join(', ') || 'không rõ'}`,
      slots: ['cpu', 'cooler'],
    });
  }

  if (
    vgaSpecs?.vgaLengthMm &&
    caseSpecs?.caseVgaLimitMm &&
    vgaSpecs.vgaLengthMm > caseSpecs.caseVgaLimitMm
  ) {
    issues.push({
      id: 'vga-case-length',
      severity: 'warning',
      title: 'Case có thể không đủ dài cho VGA',
      detail: `VGA: ${vgaSpecs.vgaLengthMm}mm | Case hỗ trợ: ${caseSpecs.caseVgaLimitMm}mm`,
      slots: ['vga', 'case'],
    });
  }

  const cpuLoad = cpuSpecs?.estimatedPowerDrawW || 0;
  const gpuLoad = vgaSpecs?.estimatedPowerDrawW || 0;
  const ramQty = Math.max(1, quantities.ram || 1);
  const storageLoad = (quantities.ssd || 0) * 6 + (quantities.hdd || 0) * 9;
  const fanLoad = (quantities.fan || 0) * 4;
  const extraLoad = 80 + ramQty * 4 + storageLoad + fanLoad;
  const estimatedLoadW = cpuLoad + gpuLoad + extraLoad;

  if (psuSpecs?.psuWatt) {
    const recommended = Math.ceil(estimatedLoadW * 1.25);
    if (psuSpecs.psuWatt < estimatedLoadW) {
      issues.push({
        id: 'psu-underload-hard',
        severity: 'error',
        title: 'Nguồn PSU có thể không đủ công suất',
        detail: `Tải ước tính ${estimatedLoadW}W, PSU hiện tại ${psuSpecs.psuWatt}W`,
        slots: ['psu', 'cpu', 'vga'],
      });
    } else if (psuSpecs.psuWatt < recommended) {
      issues.push({
        id: 'psu-underload-soft',
        severity: 'warning',
        title: 'Nên nâng PSU để có headroom an toàn',
        detail: `Tải ước tính ${estimatedLoadW}W, khuyến nghị >= ${recommended}W`,
        slots: ['psu'],
      });
    }
  }

  const requiredSlots: PcSlotKey[] = ['cpu', 'mainboard', 'ram', 'ssd', 'psu', 'case'];
  for (const slot of requiredSlots) {
    if (!selected[slot]) {
      issues.push({
        id: `missing-${slot}`,
        severity: 'info',
        title: `Thiếu linh kiện bắt buộc: ${SLOT_MAP[slot].label}`,
        detail: 'Cấu hình chưa đủ để chốt báo giá hoàn chỉnh.',
        slots: [slot],
      });
    }
  }

  return { issues, estimatedLoadW };
}

export function scoreProductForSlot(product: PcBuilderProduct, slot: PcSlotKey): number {
  const slotConfig = SLOT_MAP[slot];
  const text = toTextArray(product).join(' ');
  let score = 0;

  const categorySlug = normalizeCatalogText(product.category_slug || '');
  const brand = normalizeCatalogText(product.brand_name || '');

  for (const hint of slotConfig.categoryHints) {
    if (containsCatalogKeyword(categorySlug, hint)) score += 7;
    if (containsCatalogKeyword(text, hint)) score += 3;
  }

  for (const kw of slotConfig.searchKeywords) {
    if (containsCatalogKeyword(text, kw)) score += 2;
  }

  if (slotConfig.excludeKeywords?.some((kw) => containsCatalogKeyword(text, kw))) {
    score -= 6;
  }

  switch (slot) {
    case 'cpu':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'cpu')) score += 5;
      break;
    case 'mainboard':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'mainboard'))
        score += 5;
      break;
    case 'ram':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'ram')) score += 5;
      break;
    case 'ssd':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'ssd')) score += 5;
      break;
    case 'hdd':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'hdd')) score += 5;
      break;
    case 'vga':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'vga')) score += 5;
      break;
    case 'case':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'case')) score += 5;
      break;
    case 'keyboard':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'keyboard'))
        score += 5;
      break;
    case 'mouse':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'mouse')) score += 5;
      break;
    case 'headset':
      if (isLikelyProductType(text, product.sku || '', product.tags.join(' '), 'headset'))
        score += 5;
      break;
    case 'psu':
      if (containsCatalogKeyword(text, '80 plus') || containsCatalogKeyword(text, 'power supply'))
        score += 5;
      break;
    case 'monitor':
      if (containsCatalogKeyword(text, 'monitor') || containsCatalogKeyword(text, 'man hinh'))
        score += 6;
      break;
    case 'cooler':
      if (
        containsCatalogKeyword(text, 'tan nhiet') ||
        containsCatalogKeyword(text, 'cooler') ||
        containsCatalogKeyword(text, 'aio')
      )
        score += 6;
      break;
    case 'fan':
      if (containsCatalogKeyword(text, 'fan') || containsCatalogKeyword(text, 'rgb')) score += 6;
      break;
  }

  if (brand.includes('intel') && slot === 'cpu') score += 1;
  if (brand.includes('amd') && slot === 'cpu') score += 1;

  return score;
}
